import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Receipt,
  Loader2,
  Search,
  Trash2,
  Truck,
  Edit2,
  CheckCircle,
  X,
  FileText,
  Link2,
  Unlink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'direct_debit', label: 'Direct Debit' },
  { value: 'card_payment', label: 'Card Payment' },
  { value: 'bank_transfer', label: 'Bank Transfer' }
];

export const PurchasesPage = () => {
  const { isAdmin, isManager, user } = useAuth();
  const isStaff = user?.user_type === 'staff';
  const { stores, selectedStore } = useStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('invoices');
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseTypes, setPurchaseTypes] = useState([]);
  const [buyingGroups, setBuyingGroups] = useState([]);
  const [storeDepartments, setStoreDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  
  // Delivery Notes State
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [deliveryNoteDialogOpen, setDeliveryNoteDialogOpen] = useState(false);
  const [editingDeliveryNote, setEditingDeliveryNote] = useState(null);
  const [deliveryNoteForm, setDeliveryNoteForm] = useState({
    store_id: '',
    delivery_date: new Date(),
    supplier_id: '',
    supplier_name: '',
    delivery_note_number: '',
    buying_group_id: '',
    buying_group_name: '',
    amount: 0,
    vat_amount: 0,
    description: ''
  });
  const [deliveryNoteLoading, setDeliveryNoteLoading] = useState(false);
  
  // Reconciliation State
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileInvoice, setReconcileInvoice] = useState(null);
  const [availableDeliveryNotes, setAvailableDeliveryNotes] = useState([]);
  const [selectedDeliveryNoteIds, setSelectedDeliveryNoteIds] = useState([]);
  const [varianceNotes, setVarianceNotes] = useState('');
  // Adjustment fields for handling fees/rebates
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentVat, setAdjustmentVat] = useState(0);
  const [adjustmentType, setAdjustmentType] = useState('add'); // 'add' for fees, 'reduce' for rebates
  
  // Department Monitoring Config
  const [storeMonitoringConfig, setStoreMonitoringConfig] = useState({
    enabled: false,
    monitoredDepartments: [],
    vatRate: 20
  });

  // Selected departments for Shop purchase (user can select which departments to add)
  const [selectedDepartmentsForBreakdown, setSelectedDepartmentsForBreakdown] = useState([]);

  const [formData, setFormData] = useState({
    store_id: '',
    date: new Date(),
    invoice_number: '',
    supplier: '',
    supplier_id: '',
    purchase_type: '',
    purchase_type_id: '',
    buying_group_id: '',
    buying_group_name: '',
    department_id: '',
    department_name: '',
    amount: 0,
    vat_amount: 0,
    payment_type: 'cash',
    description: '',
    // Department breakdown for monitoring
    department_breakdown: [],
    has_department_breakdown: false
  });

  useEffect(() => {
    fetchPurchaseTypes();
    fetchBuyingGroups();
  }, []);

  useEffect(() => {
    if (stores.length > 0) {
      const storeId = selectedStore?.id || stores[0].id;
      setSelectedStoreId(storeId);
      setFormData(prev => ({ ...prev, store_id: storeId }));
      // Load departments and suppliers for the selected store
      fetchStoreDepartments(storeId);
      fetchSuppliers(storeId);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchPurchases();
      fetchDeliveryNotes();
    }
  }, [selectedStoreId, dateRange]);

  const fetchSuppliers = async (storeId) => {
    try {
      const params = storeId ? { store_id: storeId } : {};
      const response = await axios.get(`${API}/suppliers`, { params });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to load suppliers');
    }
  };

  const fetchPurchaseTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/purchase-types`);
      setPurchaseTypes((response.data || []).filter(t => t.is_active !== false));
    } catch (error) {
      console.error('Failed to load purchase types');
    }
  };

  const fetchBuyingGroups = async () => {
    try {
      const response = await axios.get(`${API}/settings/buying-groups`);
      setBuyingGroups((response.data || []).filter(g => g.is_active !== false));
    } catch (error) {
      console.error('Failed to load buying groups');
    }
  };

  const fetchStoreDepartments = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await axios.get(`${API}/stores/${storeId}`);
      const store = response.data;
      // Get departments from store config
      const departments = store.departments || [];
      setStoreDepartments(departments);
      
      // Get department monitoring config
      setStoreMonitoringConfig({
        enabled: store.department_monitoring_enabled || false,
        monitoredDepartments: store.monitored_departments || [],
        vatRate: store.purchase_vat_rate || 20
      });
    } catch (error) {
      console.error('Failed to load store departments');
      setStoreDepartments([]);
      setStoreMonitoringConfig({ enabled: false, monitoredDepartments: [], vatRate: 20 });
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const params = { store_id: selectedStoreId };
      if (dateRange.start) params.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) params.end_date = format(dateRange.end, 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/purchases`, { params });
      setPurchases(response.data);
    } catch (error) {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  // ========== DELIVERY NOTES FUNCTIONS ==========
  const fetchDeliveryNotes = async () => {
    if (!selectedStoreId) return;
    setDeliveryNoteLoading(true);
    try {
      const params = { store_id: selectedStoreId };
      if (dateRange.start) params.start_date = format(dateRange.start, 'yyyy-MM-dd');
      if (dateRange.end) params.end_date = format(dateRange.end, 'yyyy-MM-dd');
      
      const response = await axios.get(`${API}/purchases/delivery-notes`, { params });
      setDeliveryNotes(response.data || []);
    } catch (error) {
      console.error('Failed to load delivery notes:', error);
      toast.error('Failed to load delivery notes');
    } finally {
      setDeliveryNoteLoading(false);
    }
  };

  const saveDeliveryNote = async () => {
    if (!deliveryNoteForm.delivery_note_number || !deliveryNoteForm.supplier_name || !deliveryNoteForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...deliveryNoteForm,
        store_id: selectedStoreId,
        delivery_date: format(deliveryNoteForm.delivery_date, 'yyyy-MM-dd'),
        amount: parseFloat(deliveryNoteForm.amount) || 0,
        vat_amount: parseFloat(deliveryNoteForm.vat_amount) || 0
      };
      
      if (editingDeliveryNote) {
        await axios.put(`${API}/purchases/delivery-notes/${editingDeliveryNote.id}`, payload);
        toast.success('Delivery note updated');
      } else {
        await axios.post(`${API}/purchases/delivery-notes`, payload);
        toast.success('Delivery note added');
      }
      
      setDeliveryNoteDialogOpen(false);
      resetDeliveryNoteForm();
      fetchDeliveryNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save delivery note');
    } finally {
      setSaving(false);
    }
  };

  const deleteDeliveryNote = async (noteId) => {
    if (!window.confirm('Delete this delivery note?')) return;
    try {
      await axios.delete(`${API}/purchases/delivery-notes/${noteId}`);
      toast.success('Delivery note deleted');
      fetchDeliveryNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete delivery note');
    }
  };

  const resetDeliveryNoteForm = () => {
    setDeliveryNoteForm({
      store_id: selectedStoreId,
      delivery_date: new Date(),
      supplier_id: '',
      supplier_name: '',
      delivery_note_number: '',
      buying_group_id: '',
      buying_group_name: '',
      amount: 0,
      vat_amount: 0,
      description: ''
    });
    setEditingDeliveryNote(null);
  };

  const handleEditDeliveryNote = (note) => {
    setEditingDeliveryNote(note);
    setDeliveryNoteForm({
      store_id: note.store_id,
      delivery_date: new Date(note.delivery_date),
      supplier_id: note.supplier_id || '',
      supplier_name: note.supplier_name,
      delivery_note_number: note.delivery_note_number,
      buying_group_id: note.buying_group_id || '',
      buying_group_name: note.buying_group_name || '',
      amount: note.amount,
      vat_amount: note.vat_amount || 0,
      description: note.description || ''
    });
    setDeliveryNoteDialogOpen(true);
  };

  // ========== RECONCILIATION FUNCTIONS ==========
  const openReconcileDialog = async (invoice) => {
    setReconcileInvoice(invoice);
    setSelectedDeliveryNoteIds(invoice.linked_delivery_note_ids || []);
    setVarianceNotes('');
    // Reset adjustment fields
    setAdjustmentAmount(0);
    setAdjustmentVat(0);
    setAdjustmentType('add');
    
    // Fetch pending delivery notes for the same buying group
    try {
      const params = { 
        store_id: selectedStoreId,
        status: 'pending'
      };
      if (invoice.buying_group_id) {
        params.buying_group_id = invoice.buying_group_id;
      }
      
      const response = await axios.get(`${API}/purchases/delivery-notes`, { params });
      
      // Also include already linked notes
      const linkedNotes = await axios.get(`${API}/purchases/delivery-notes`, {
        params: { store_id: selectedStoreId }
      });
      const linkedIds = invoice.linked_delivery_note_ids || [];
      const alreadyLinked = (linkedNotes.data || []).filter(n => linkedIds.includes(n.id));
      
      // Combine and dedupe
      const allNotes = [...(response.data || [])];
      alreadyLinked.forEach(n => {
        if (!allNotes.find(existing => existing.id === n.id)) {
          allNotes.push(n);
        }
      });
      
      setAvailableDeliveryNotes(allNotes.sort((a, b) => b.delivery_date.localeCompare(a.delivery_date)));
    } catch (error) {
      console.error('Failed to load delivery notes for reconciliation:', error);
      setAvailableDeliveryNotes([]);
    }
    
    setReconcileDialogOpen(true);
  };

  const handleReconcile = async () => {
    if (selectedDeliveryNoteIds.length === 0) {
      toast.error('Please select at least one delivery note');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/purchases/delivery-notes/reconcile`, {
        invoice_id: reconcileInvoice.id,
        invoice_number: reconcileInvoice.invoice_number,
        delivery_note_ids: selectedDeliveryNoteIds,
        variance_notes: varianceNotes
      });
      
      toast.success('Delivery notes reconciled with invoice');
      setReconcileDialogOpen(false);
      fetchPurchases();
      fetchDeliveryNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reconcile');
    } finally {
      setSaving(false);
    }
  };

  const handleUnreconcile = async (invoiceId) => {
    if (!window.confirm('Unlink all delivery notes from this invoice?')) return;
    
    try {
      await axios.post(`${API}/purchases/delivery-notes/unreconcile`, {
        invoice_id: invoiceId
      });
      toast.success('Delivery notes unlinked');
      fetchPurchases();
      fetchDeliveryNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unreconcile');
    }
  };

  // Fuel Card Reconciliation - Navigate to Card Balance Dashboard
  const openFuelReconcileDialog = (invoice) => {
    // Navigate to Card Balance Dashboard with the invoice ID for linking
    navigate(`/fuel-card-balances?link_to_statement=${invoice.id}`);
  };

  const handleFuelUnreconcile = async (invoiceId) => {
    if (!window.confirm('Unlink all fuel transactions from this credit note?')) return;
    
    try {
      await axios.post(`${API}/purchases/fuel-transactions/unreconcile`, {
        credit_note_id: invoiceId
      });
      toast.success('Fuel transactions unlinked');
      fetchPurchases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unreconcile');
    }
  };

  const toggleDeliveryNoteSelection = (noteId) => {
    setSelectedDeliveryNoteIds(prev => 
      prev.includes(noteId) 
        ? prev.filter(id => id !== noteId)
        : [...prev, noteId]
    );
  };

  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setFormData({ ...formData, supplier_id: supplier.id, supplier: supplier.name });
    }
  };

  const handlePurchaseTypeSelect = (typeId) => {
    if (typeId === 'none') {
      setFormData({ 
        ...formData, 
        purchase_type_id: '', 
        purchase_type: '',
        buying_group_id: '',
        buying_group_name: '',
        supplier: '',
        supplier_id: ''
      });
    } else {
      const type = purchaseTypes.find(t => t.id === typeId);
      if (type) {
        // If Buying Group type, clear supplier fields (will be set when buying group is selected)
        // If other type, clear buying group fields
        const isBuyingGroup = type.name.toLowerCase() === 'buying group';
        setFormData({ 
          ...formData, 
          purchase_type_id: type.id, 
          purchase_type: type.name,
          buying_group_id: '',
          buying_group_name: '',
          supplier: isBuyingGroup ? '' : formData.supplier,
          supplier_id: isBuyingGroup ? '' : formData.supplier_id
        });
      }
    }
  };

  const handleBuyingGroupSelect = (groupId) => {
    if (groupId === 'none') {
      setFormData({ ...formData, buying_group_id: '', buying_group_name: '', supplier: '', supplier_id: '' });
    } else {
      const group = buyingGroups.find(g => g.id === groupId);
      if (group) {
        // When Buying Group is selected, it becomes the supplier
        setFormData({ 
          ...formData, 
          buying_group_id: group.id, 
          buying_group_name: group.name,
          supplier: group.name,  // Buying group name is the supplier
          supplier_id: ''        // No separate supplier_id for buying group
        });
      }
    }
  };

  const handleDepartmentSelect = (deptId) => {
    if (deptId === 'none') {
      setFormData({ ...formData, department_id: '', department_name: '' });
    } else {
      const dept = storeDepartments.find(d => d.id === deptId);
      if (dept) {
        setFormData({ 
          ...formData, 
          department_id: dept.id, 
          department_name: dept.name 
        });
      }
    }
  };

  const handleStoreChangeInForm = async (storeId) => {
    // Fetch store config first
    try {
      const response = await axios.get(`${API}/stores/${storeId}`);
      const store = response.data;
      
      const monitoringEnabled = store.department_monitoring_enabled || false;
      const monitoredDepts = store.monitored_departments || [];
      const vatRate = store.purchase_vat_rate || 20;
      
      setStoreMonitoringConfig({
        enabled: monitoringEnabled,
        monitoredDepartments: monitoredDepts,
        vatRate: vatRate
      });
      setStoreDepartments(store.departments || []);
      
      // Reset selected departments - user will select which ones they need
      setSelectedDepartmentsForBreakdown([]);
      
      // Fetch suppliers for the new store
      fetchSuppliers(storeId);
      
      setFormData({ 
        ...formData, 
        store_id: storeId, 
        department_id: '', 
        department_name: '',
        supplier_id: '',
        supplier: '',
        department_breakdown: [],
        has_department_breakdown: monitoringEnabled
      });
    } catch (error) {
      console.error('Failed to load store config');
      setFormData({ ...formData, store_id: storeId, department_id: '', department_name: '' });
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    
    // Restore selected departments from existing breakdown
    const existingBreakdown = purchase.department_breakdown || [];
    const selectedDeptIds = existingBreakdown
      .filter(b => b.department_id !== 'other' && (b.amount_ex_vat > 0 || b.vat_amount > 0))
      .map(b => b.department_id);
    setSelectedDepartmentsForBreakdown(selectedDeptIds);
    
    setFormData({
      store_id: purchase.store_id,
      date: new Date(purchase.date),
      invoice_number: purchase.invoice_number || '',
      supplier: purchase.supplier || '',
      supplier_id: purchase.supplier_id || '',
      purchase_type: purchase.purchase_type || '',
      purchase_type_id: purchase.purchase_type_id || '',
      buying_group_id: purchase.buying_group_id || '',
      buying_group_name: purchase.buying_group_name || '',
      department_id: purchase.department_id || '',
      department_name: purchase.department_name || '',
      amount: purchase.amount || 0,
      vat_amount: purchase.vat_amount || 0,
      payment_type: purchase.payment_type || 'cash',
      description: purchase.description || '',
      department_breakdown: purchase.department_breakdown || [],
      has_department_breakdown: purchase.has_department_breakdown || false
    });
    fetchStoreDepartments(purchase.store_id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.purchase_type_id || formData.purchase_type_id === 'none') {
      toast.error('Please select a Purchase Type');
      setSaving(false);
      return;
    }
    
    setSaving(true);

    try {
      // Build payload
      let departmentBreakdown = formData.department_breakdown || [];
      
      // If purchase type is "Shop" or "Buying Group" and department monitoring is enabled, add "Other" department to breakdown
      const isShopPurchase = formData.purchase_type?.toLowerCase() === 'shop';
      const isBuyingGroupPurchase = formData.purchase_type?.toLowerCase() === 'buying group';
      const shouldHaveDeptBreakdown = (isShopPurchase || isBuyingGroupPurchase) && storeMonitoringConfig.enabled && storeMonitoringConfig.monitoredDepartments.length > 0;
      
      if (shouldHaveDeptBreakdown) {
        const allocatedAmount = departmentBreakdown.reduce((sum, b) => sum + (b.amount_ex_vat || 0), 0);
        const allocatedVat = departmentBreakdown.reduce((sum, b) => sum + (b.vat_amount || 0), 0);
        const otherAmount = Math.max(0, (formData.amount || 0) - allocatedAmount);
        // Other VAT = Invoice VAT - Sum of Department VATs
        const otherVat = Math.max(0, (formData.vat_amount || 0) - allocatedVat);
        const otherTotal = otherAmount + otherVat;
        
        // Add "Other" department entry
        departmentBreakdown = [
          ...departmentBreakdown.filter(b => b.department_id !== 'other'),
          {
            department_id: 'other',
            department_name: 'Other',
            amount_ex_vat: otherAmount,
            vat_amount: otherVat,
            total: otherTotal,
            vat_rate: 'auto'
          }
        ];
      }
      
      const payload = {
        ...formData,
        date: format(formData.date, 'yyyy-MM-dd'),
        department_breakdown: departmentBreakdown,
        has_department_breakdown: shouldHaveDeptBreakdown
      };

      if (editingPurchase) {
        await axios.put(`${API}/purchases/${editingPurchase.id}`, payload);
        toast.success('Purchase updated successfully!');
      } else {
        await axios.post(`${API}/purchases`, payload);
        toast.success('Purchase recorded successfully!');
      }
      setDialogOpen(false);
      setEditingPurchase(null);
      fetchPurchases();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to delete this purchase?')) return;
    
    try {
      await axios.delete(`${API}/purchases/${purchaseId}`);
      toast.success('Purchase deleted');
      fetchPurchases();
    } catch (error) {
      toast.error('Failed to delete purchase');
    }
  };

  const resetForm = () => {
    // Reset selected departments - user will select which ones they need
    setSelectedDepartmentsForBreakdown([]);
    
    setFormData({
      store_id: selectedStoreId,
      date: new Date(),
      invoice_number: '',
      supplier: '',
      supplier_id: '',
      purchase_type: '',
      purchase_type_id: '',
      buying_group_id: '',
      buying_group_name: '',
      department_id: '',
      department_name: '',
      amount: 0,
      vat_amount: 0,
      payment_type: 'cash',
      description: '',
      department_breakdown: [],
      has_department_breakdown: storeMonitoringConfig.enabled
    });
    setEditingPurchase(null);
  };

  const filteredPurchases = purchases.filter(p => {
    const supplier = p.supplier || p.supplier_name || '';
    const invoiceNum = p.invoice_number || '';
    return supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
           invoiceNum.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const totalAmount = filteredPurchases.reduce((sum, p) => sum + (p.total_amount || p.amount), 0);

  // Calculate form total
  const formTotal = (parseFloat(formData.amount) || 0) + (parseFloat(formData.vat_amount) || 0);

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Purchase Invoices
          </h1>
          <p className="text-muted-foreground mt-1">
            Record and manage supplier invoices
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
              <SelectTrigger data-testid="purchase-store-select">
                <SelectValue placeholder="Select Store" />
              </SelectTrigger>
              <SelectContent>
                {stores.map(store => (
                  <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search supplier or invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="purchase-search"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.start ? format(dateRange.start, 'PP') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateRange.start}
                  onSelect={(date) => setDateRange({ ...dateRange, start: date })}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.end ? format(dateRange.end, 'PP') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateRange.end}
                  onSelect={(date) => setDateRange({ ...dateRange, end: date })}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Invoices and Delivery Notes */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="delivery-notes" className="flex items-center gap-2">
            <Truck className="h-4 w-4" />
            Delivery Notes
          </TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          {/* Tab Header with Add Invoice Button */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>Invoices</h2>
              <p className="text-sm text-muted-foreground">Record and manage supplier invoices</p>
            </div>
            <Button onClick={async () => {
              if (selectedStoreId) {
                await handleStoreChangeInForm(selectedStoreId);
              }
              setDialogOpen(true);
            }} data-testid="add-purchase-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Invoice
            </Button>
          </div>

          {/* Summary */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Purchases</p>
                  <p className="text-2xl font-bold font-mono">£{totalAmount.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Invoices</p>
                  <p className="text-2xl font-bold">{filteredPurchases.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

      {/* Purchases List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner" />
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Receipt className="h-12 w-12 mb-4 opacity-50" />
              <p>No purchases found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Invoice #</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Supplier</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Amount</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">VAT</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">Total</th>
                    <th className="text-center p-4 font-medium text-muted-foreground">Status</th>
                    {(isAdmin || isManager || isStaff) && (
                      <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchases.map((purchase) => (
                    <tr key={purchase.id} className="border-b border-border/50 table-row-hover">
                      <td className="p-4">
                        {new Date(purchase.date).toLocaleDateString('en-GB')}
                      </td>
                      <td className="p-4 font-mono">{purchase.invoice_number || '-'}</td>
                      <td className="p-4">{purchase.supplier || purchase.supplier_name || '-'}</td>
                      <td className="p-4">
                        {purchase.purchase_type ? (
                          <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                            {purchase.purchase_type}
                          </span>
                        ) : '-'}
                      </td>
                      <td className={`p-4 text-right font-mono ${(purchase.amount || 0) < 0 ? 'text-red-500' : ''}`}>£{(purchase.amount || 0).toFixed(2)}</td>
                      <td className={`p-4 text-right font-mono ${(purchase.vat_amount || purchase.vat || 0) < 0 ? 'text-red-500' : ''}`}>£{(purchase.vat_amount || purchase.vat || 0).toFixed(2)}</td>
                      <td className={`p-4 text-right font-mono font-semibold ${(purchase.total_amount || purchase.amount || 0) < 0 ? 'text-red-500' : ''}`}>£{(purchase.total_amount || purchase.amount || 0).toFixed(2)}</td>
                      <td className="p-4 text-center">
                        {purchase.is_reconciled ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
                            <CheckCircle className="h-3 w-3" />
                            Reconciled
                          </span>
                        ) : purchase.purchase_type?.toLowerCase() === 'buying group' ? (
                          purchase.linked_delivery_note_ids?.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-500/20 text-blue-500 px-2 py-1 rounded">
                              <Link2 className="h-3 w-3" />
                              {purchase.linked_delivery_note_ids.length} Notes Linked
                            </span>
                          ) : (
                            <span className="text-xs text-amber-500">Pending Reconcile</span>
                          )
                        ) : purchase.purchase_type?.toLowerCase() === 'fuel' ? (
                          purchase.reconciliation_status === 'reconciled' ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
                              <CheckCircle className="h-3 w-3" />
                              Transactions Linked
                            </span>
                          ) : (
                            <span className="text-xs text-amber-500">Pending Reconcile</span>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                      {(isAdmin || isManager || isStaff) && (
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(purchase)}
                              disabled={purchase.is_reconciled}
                              className={purchase.is_reconciled ? 'opacity-50 cursor-not-allowed' : ''}
                              title={purchase.is_reconciled ? 'Cannot edit reconciled invoice' : 'Edit'}
                              data-testid={`edit-purchase-${purchase.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {/* Reconcile button for Buying Group invoices */}
                            {purchase.purchase_type?.toLowerCase() === 'buying group' && (
                              purchase.linked_delivery_note_ids?.length > 0 ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleUnreconcile(purchase.id)}
                                  title="Unlink delivery notes"
                                  className="text-amber-500"
                                >
                                  <Unlink className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openReconcileDialog(purchase)}
                                  title="Reconcile with delivery notes"
                                  className="text-primary"
                                >
                                  <Link2 className="h-4 w-4" />
                                </Button>
                              )
                            )}
                            {/* Reconcile button for Fuel invoices/credit notes */}
                            {purchase.purchase_type?.toLowerCase() === 'fuel' && (
                              purchase.reconciliation_status === 'reconciled' ? (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleFuelUnreconcile(purchase.id)}
                                  title="Unlink fuel transactions"
                                  className="text-amber-500"
                                >
                                  <Unlink className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openFuelReconcileDialog(purchase)}
                                  title="Reconcile with fuel card transactions"
                                  className="text-primary"
                                >
                                  <Link2 className="h-4 w-4" />
                                </Button>
                              )
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(purchase.id)}
                                disabled={purchase.is_reconciled}
                                className={`text-destructive ${purchase.is_reconciled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={purchase.is_reconciled ? 'Cannot delete reconciled invoice' : 'Delete'}
                                data-testid={`delete-purchase-${purchase.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Delivery Notes Tab */}
        <TabsContent value="delivery-notes" className="space-y-4 mt-4">
          {/* Delivery Notes Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Barlow Condensed' }}>Delivery Notes</h2>
              <p className="text-sm text-muted-foreground">Record supplier delivery notes for reconciliation with buying group invoices</p>
            </div>
            <Button onClick={() => { resetDeliveryNoteForm(); setDeliveryNoteDialogOpen(true); }} data-testid="add-delivery-note-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Delivery Note
            </Button>
          </div>

          {/* Delivery Notes Summary */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Notes</p>
                <p className="text-2xl font-bold">{deliveryNotes.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-amber-500">{deliveryNotes.filter(n => n.status === 'pending').length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Reconciled</p>
                <p className="text-2xl font-bold text-green-500">{deliveryNotes.filter(n => n.status === 'reconciled').length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Delivery Notes List */}
          <Card>
            <CardContent className="p-0">
              {deliveryNoteLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : deliveryNotes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Truck className="h-12 w-12 mb-4 opacity-50" />
                  <p>No delivery notes found</p>
                  <p className="text-sm mt-2">Add a delivery note to track supplier deliveries</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Note #</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Supplier</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Buying Group</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">VAT</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Total</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryNotes.map((note) => (
                        <tr key={note.id} className="border-b border-border/50 hover:bg-muted/20">
                          <td className="p-4">{new Date(note.delivery_date).toLocaleDateString('en-GB')}</td>
                          <td className="p-4 font-mono">{note.delivery_note_number}</td>
                          <td className="p-4">{note.supplier_name}</td>
                          <td className="p-4">{note.buying_group_name || '-'}</td>
                          <td className="p-4 text-right font-mono">£{(note.amount || 0).toFixed(2)}</td>
                          <td className="p-4 text-right font-mono">£{(note.vat_amount || 0).toFixed(2)}</td>
                          <td className="p-4 text-right font-mono font-semibold">£{(note.total_amount || 0).toFixed(2)}</td>
                          <td className="p-4 text-center">
                            {note.status === 'reconciled' ? (
                              <span className="px-2 py-1 bg-green-500/20 text-green-500 text-xs rounded-full flex items-center gap-1 justify-center">
                                <CheckCircle className="h-3 w-3" />
                                Reconciled
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-amber-500/20 text-amber-500 text-xs rounded-full">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            {note.status === 'pending' ? (
                              <div className="flex justify-end gap-2">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleEditDeliveryNote(note)}
                                  title="Edit"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-destructive"
                                  onClick={() => deleteDeliveryNote(note.id)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Linked to: {note.linked_invoice_number}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Purchase Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingPurchase ? 'Edit Purchase Invoice' : 'Add Purchase Invoice'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Store & Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Store</Label>
                <Select 
                  value={formData.store_id} 
                  onValueChange={handleStoreChangeInForm}
                >
                  <SelectTrigger data-testid="purchase-form-store">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => date && setFormData({ ...formData, date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Row 2: Purchase Type - REQUIRED (moved up) */}
            <div className="space-y-2">
              <Label>Purchase Type <span className="text-red-500">*</span></Label>
              <Select 
                value={formData.purchase_type_id || 'none'} 
                onValueChange={handlePurchaseTypeSelect}
                required
              >
                <SelectTrigger data-testid="purchase-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select Type</SelectItem>
                  {[...purchaseTypes].sort((a, b) => a.name.localeCompare(b.name)).map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 3: Conditional - Buying Group OR Supplier based on Purchase Type */}
            <div className="grid grid-cols-2 gap-4">
              {formData.purchase_type?.toLowerCase() === 'buying group' ? (
                // Show Buying Group selector (acts as supplier)
                <div className="space-y-2">
                  <Label>Buying Group (Supplier) <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.buying_group_id || 'none'} 
                    onValueChange={handleBuyingGroupSelect}
                    required
                  >
                    <SelectTrigger data-testid="buying-group-select">
                      <SelectValue placeholder="Select buying group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select Buying Group</SelectItem>
                      {buyingGroups.map(group => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                // Show normal Supplier selector for other purchase types
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select 
                    value={formData.supplier_id || ''} 
                    onValueChange={handleSupplierSelect}
                  >
                    <SelectTrigger data-testid="purchase-supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input
                  value={formData.invoice_number}
                  onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  required
                  data-testid="purchase-invoice-input"
                />
              </div>
            </div>

            {/* Invoice Amount Section - Always shown */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Invoice Amount (Ex VAT) £</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                  required
                  className="font-mono"
                  data-testid="purchase-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>VAT Amount £</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.vat_amount || ''}
                  onChange={(e) => setFormData({ ...formData, vat_amount: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                  data-testid="purchase-vat-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Invoice Total (Inc VAT)</Label>
                <div className="h-10 px-3 py-2 bg-muted rounded-md font-mono font-semibold flex items-center">
                  £{formTotal.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Department Breakdown - When Purchase Type is "Shop" or "Buying Group" and monitoring enabled */}
            {(formData.purchase_type?.toLowerCase() === 'shop' || formData.purchase_type?.toLowerCase() === 'buying group') && storeMonitoringConfig.enabled && storeMonitoringConfig.monitoredDepartments.length > 0 && (
              <div className="border border-primary/30 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-primary">Department Cost Breakdown</h4>
                  <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                    {formData.purchase_type} Purchase
                  </span>
                </div>
                
                {/* Two Column Layout: Available Departments | Selected Departments */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left Column: Available Departments List */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Available Departments</Label>
                    <div className="border rounded-md max-h-48 overflow-y-auto">
                      {[...storeMonitoringConfig.monitoredDepartments]
                        .filter(dept => !selectedDepartmentsForBreakdown.includes(dept.department_id))
                        .sort((a, b) => a.department_name.localeCompare(b.department_name))
                        .map((dept) => (
                          <div 
                            key={dept.department_id} 
                            className="flex items-center justify-between p-2 hover:bg-muted/50 border-b last:border-b-0 cursor-pointer"
                            onClick={() => {
                              // Add department to selected list
                              setSelectedDepartmentsForBreakdown([...selectedDepartmentsForBreakdown, dept.department_id]);
                              // Initialize breakdown entry
                              const newBreakdown = [...formData.department_breakdown, {
                                department_id: dept.department_id,
                                department_name: dept.department_name,
                                amount_ex_vat: 0,
                                vat_amount: 0,
                                total: 0,
                                vat_rate: dept.vat_rate !== undefined ? dept.vat_rate : 20
                              }];
                              setFormData({ ...formData, department_breakdown: newBreakdown });
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{dept.department_name}</span>
                              <span className="text-xs bg-muted px-1.5 py-0.5 rounded">{dept.vat_rate !== undefined ? dept.vat_rate : 20}%</span>
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs">
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                        ))}
                      {storeMonitoringConfig.monitoredDepartments.filter(dept => !selectedDepartmentsForBreakdown.includes(dept.department_id)).length === 0 && (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          All departments added
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Column: Selected Departments with Input Fields */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Selected Departments</Label>
                    <div className="space-y-2">
                      {/* Header */}
                      {selectedDepartmentsForBreakdown.length > 0 && (
                        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground px-2">
                          <span className="col-span-3">Department</span>
                          <span className="col-span-4 text-right">Amount (Ex VAT)</span>
                          <span className="col-span-4 text-right">VAT</span>
                          <span className="col-span-1 text-right"></span>
                        </div>
                      )}
                      
                      {/* Selected Department Rows */}
                      {selectedDepartmentsForBreakdown.map((deptId) => {
                        const dept = storeMonitoringConfig.monitoredDepartments.find(d => d.department_id === deptId);
                        if (!dept) return null;
                        
                        const breakdown = formData.department_breakdown.find(b => b.department_id === deptId) || {
                          amount_ex_vat: 0,
                          vat_amount: 0,
                          total: 0
                        };
                        const vatRate = (dept.vat_rate !== undefined ? dept.vat_rate : 20) / 100;
                        
                        return (
                          <div key={deptId} className="grid grid-cols-12 gap-2 items-center p-2 bg-muted/30 rounded">
                            <div className="col-span-3 flex items-center gap-1">
                              <span className="font-medium text-sm truncate">{dept.department_name}</span>
                              <span className="text-xs bg-muted px-1 rounded flex-shrink-0">{dept.vat_rate !== undefined ? dept.vat_rate : 20}%</span>
                            </div>
                            <div className="col-span-4">
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="font-mono text-right h-8 text-sm w-full"
                                value={breakdown.amount_ex_vat || ''}
                                onChange={(e) => {
                                  const amount = parseFloat(e.target.value) || 0;
                                  const vat = amount * vatRate;
                                  const total = amount + vat;
                                  
                                  let newBreakdown = [...formData.department_breakdown];
                                  const existingIdx = newBreakdown.findIndex(b => b.department_id === deptId);
                                  
                                  if (existingIdx >= 0) {
                                    newBreakdown[existingIdx] = {
                                      ...newBreakdown[existingIdx],
                                      amount_ex_vat: amount,
                                      vat_amount: vat,
                                      total: total,
                                      vat_rate: dept.vat_rate !== undefined ? dept.vat_rate : 20
                                    };
                                  } else {
                                    newBreakdown.push({
                                      department_id: deptId,
                                      department_name: dept.department_name,
                                      amount_ex_vat: amount,
                                      vat_amount: vat,
                                      total: total,
                                      vat_rate: dept.vat_rate !== undefined ? dept.vat_rate : 20
                                    });
                                  }
                                  
                                  setFormData({ ...formData, department_breakdown: newBreakdown });
                                }}
                              />
                            </div>
                            <span className="col-span-4 font-mono text-right text-sm text-muted-foreground">
                              £{(breakdown.vat_amount || 0).toFixed(2)}
                            </span>
                            <div className="col-span-1 flex justify-end">
                              <Button 
                                type="button" 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-destructive"
                                onClick={() => {
                                  // Remove from selected
                                  setSelectedDepartmentsForBreakdown(selectedDepartmentsForBreakdown.filter(id => id !== deptId));
                                  // Remove from breakdown
                                  setFormData({
                                    ...formData,
                                    department_breakdown: formData.department_breakdown.filter(b => b.department_id !== deptId)
                                  });
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      
                      {selectedDepartmentsForBreakdown.length === 0 && (
                        <div className="p-4 border border-dashed rounded text-center text-sm text-muted-foreground">
                          Click on a department from the left to add it
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Separator */}
                <div className="border-t border-border pt-4 space-y-2">
                  {/* Other Department (Auto-calculated) - Always shown */}
                  {(() => {
                    const allocatedAmount = formData.department_breakdown.reduce((sum, b) => sum + (b.amount_ex_vat || 0), 0);
                    const allocatedVat = formData.department_breakdown.reduce((sum, b) => sum + (b.vat_amount || 0), 0);
                    const otherAmount = Math.max(0, (formData.amount || 0) - allocatedAmount);
                    const otherVat = Math.max(0, (formData.vat_amount || 0) - allocatedVat);
                    const otherTotal = otherAmount + otherVat;
                    
                    return (
                      <div className="grid grid-cols-4 gap-2 items-center p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                        <span className="font-medium text-sm text-blue-400">Other (Auto)</span>
                        <span className="font-mono text-right text-sm">
                          £{otherAmount.toFixed(2)}
                        </span>
                        <span className="font-mono text-right text-sm text-muted-foreground">
                          £{otherVat.toFixed(2)}
                        </span>
                        <span className="font-mono text-right text-sm font-semibold">
                          £{otherTotal.toFixed(2)}
                        </span>
                      </div>
                    );
                  })()}
                  
                  {/* Totals Row */}
                  {(() => {
                    const allocatedAmount = formData.department_breakdown.reduce((sum, b) => sum + (b.amount_ex_vat || 0), 0);
                    const allocatedVat = formData.department_breakdown.reduce((sum, b) => sum + (b.vat_amount || 0), 0);
                    const otherAmount = Math.max(0, (formData.amount || 0) - allocatedAmount);
                    const otherVat = Math.max(0, (formData.vat_amount || 0) - allocatedVat);
                    const totalAllocated = allocatedAmount + otherAmount;
                    const totalVat = allocatedVat + otherVat;
                    
                    return (
                      <div className="grid grid-cols-4 gap-2 items-center p-3 bg-muted/50 rounded font-semibold">
                        <span className="text-sm">Total</span>
                        <span className="font-mono text-right text-sm">
                          £{totalAllocated.toFixed(2)}
                        </span>
                        <span className="font-mono text-right text-sm">
                          £{totalVat.toFixed(2)}
                        </span>
                        <span className="font-mono text-right text-sm text-primary">
                          £{(totalAllocated + totalVat).toFixed(2)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Description (Optional) */}
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Notes about this purchase"
                data-testid="purchase-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingPurchase ? 'Update' : 'Save'} Purchase
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delivery Note Dialog */}
      <Dialog open={deliveryNoteDialogOpen} onOpenChange={(open) => { setDeliveryNoteDialogOpen(open); if (!open) resetDeliveryNoteForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingDeliveryNote ? 'Edit Delivery Note' : 'Add Delivery Note'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Delivery Date <span className="text-red-500">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(deliveryNoteForm.delivery_date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={deliveryNoteForm.delivery_date}
                      onSelect={(date) => date && setDeliveryNoteForm({ ...deliveryNoteForm, delivery_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Delivery Note # <span className="text-red-500">*</span></Label>
                <Input
                  value={deliveryNoteForm.delivery_note_number}
                  onChange={(e) => setDeliveryNoteForm({ ...deliveryNoteForm, delivery_note_number: e.target.value })}
                  placeholder="e.g., DN-12345"
                  data-testid="delivery-note-number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Supplier <span className="text-red-500">*</span></Label>
                <Select 
                  value={deliveryNoteForm.supplier_id || ''} 
                  onValueChange={(val) => {
                    const supplier = suppliers.find(s => s.id === val);
                    setDeliveryNoteForm({ 
                      ...deliveryNoteForm, 
                      supplier_id: val, 
                      supplier_name: supplier?.name || '' 
                    });
                  }}
                >
                  <SelectTrigger data-testid="delivery-note-supplier">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...suppliers].sort((a, b) => a.name.localeCompare(b.name)).map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Buying Group</Label>
                <Select 
                  value={deliveryNoteForm.buying_group_id || 'none'} 
                  onValueChange={(val) => {
                    if (val === 'none') {
                      setDeliveryNoteForm({ ...deliveryNoteForm, buying_group_id: '', buying_group_name: '' });
                    } else {
                      const group = buyingGroups.find(g => g.id === val);
                      setDeliveryNoteForm({ ...deliveryNoteForm, buying_group_id: val, buying_group_name: group?.name || '' });
                    }
                  }}
                >
                  <SelectTrigger data-testid="delivery-note-buying-group">
                    <SelectValue placeholder="Select buying group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {buyingGroups.map(group => (
                      <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Amount (Ex VAT) <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={deliveryNoteForm.amount}
                  onChange={(e) => setDeliveryNoteForm({ ...deliveryNoteForm, amount: parseFloat(e.target.value) || 0 })}
                  data-testid="delivery-note-amount"
                />
              </div>

              <div className="space-y-2">
                <Label>VAT Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={deliveryNoteForm.vat_amount}
                  onChange={(e) => setDeliveryNoteForm({ ...deliveryNoteForm, vat_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="delivery-note-vat"
                />
              </div>

              <div className="space-y-2">
                <Label>Total</Label>
                <Input
                  type="text"
                  value={`£${((deliveryNoteForm.amount || 0) + (deliveryNoteForm.vat_amount || 0)).toFixed(2)}`}
                  disabled
                  className="bg-muted"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={deliveryNoteForm.description}
                onChange={(e) => setDeliveryNoteForm({ ...deliveryNoteForm, description: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveryNoteDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveDeliveryNote} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingDeliveryNote ? 'Update' : 'Save'} Delivery Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={reconcileDialogOpen} onOpenChange={setReconcileDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              Reconcile Invoice with Delivery Notes
            </DialogTitle>
          </DialogHeader>

          {reconcileInvoice && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Invoice: {reconcileInvoice.invoice_number}</p>
                    <p className="text-sm text-muted-foreground">
                      {reconcileInvoice.supplier} • {new Date(reconcileInvoice.date).toLocaleDateString('en-GB')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold font-mono">£{(reconcileInvoice.total_amount || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Invoice Total</p>
                  </div>
                </div>
              </div>

              {/* Available Delivery Notes */}
              <div>
                <Label className="mb-2 block">Select Delivery Notes to Link:</Label>
                {availableDeliveryNotes.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No pending delivery notes found for this buying group.
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="p-2 text-left">Select</th>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Note #</th>
                          <th className="p-2 text-left">Supplier</th>
                          <th className="p-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableDeliveryNotes.map(note => (
                          <tr key={note.id} className={`border-b hover:bg-muted/20 ${selectedDeliveryNoteIds.includes(note.id) ? 'bg-primary/10' : ''}`}>
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selectedDeliveryNoteIds.includes(note.id)}
                                onChange={() => toggleDeliveryNoteSelection(note.id)}
                                className="rounded"
                              />
                            </td>
                            <td className="p-2">{new Date(note.delivery_date).toLocaleDateString('en-GB')}</td>
                            <td className="p-2 font-mono">{note.delivery_note_number}</td>
                            <td className="p-2">{note.supplier_name}</td>
                            <td className="p-2 text-right font-mono">£{(note.total_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Summary */}
              {selectedDeliveryNoteIds.length > 0 && (
                <div className="space-y-4">
                  {/* Adjustment Section - For handling fees or rebates */}
                  <div className="p-4 border border-primary/30 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-primary">Adjustment (Handling Fee / Rebate)</h4>
                      <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="add">Add (Fee)</SelectItem>
                          <SelectItem value="reduce">Reduce (Rebate)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Amount (Ex VAT) £</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adjustmentAmount || ''}
                          onChange={(e) => setAdjustmentAmount(parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">VAT £</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={adjustmentVat || ''}
                          onChange={(e) => setAdjustmentVat(parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          className="font-mono"
                        />
                      </div>
                    </div>
                    {(adjustmentAmount > 0 || adjustmentVat > 0) && (
                      <p className="text-xs text-muted-foreground">
                        {adjustmentType === 'add' 
                          ? `Fee £${((adjustmentAmount || 0) + (adjustmentVat || 0)).toFixed(2)}: adds to delivery notes (invoice > notes)` 
                          : `Rebate £${((adjustmentAmount || 0) + (adjustmentVat || 0)).toFixed(2)}: adds to invoice (invoice < notes)`}
                      </p>
                    )}
                  </div>

                  {/* Totals Summary */}
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Delivery Notes Total</p>
                        {(() => {
                          const notesTotal = availableDeliveryNotes
                            .filter(n => selectedDeliveryNoteIds.includes(n.id))
                            .reduce((sum, n) => sum + (n.total_amount || 0), 0);
                          const adjustmentTotal = (adjustmentAmount || 0) + (adjustmentVat || 0);
                          // Fee: ADD to delivery notes (buying group charges extra)
                          const displayTotal = adjustmentType === 'add' ? notesTotal + adjustmentTotal : notesTotal;
                          return (
                            <>
                              <p className="text-xl font-bold font-mono">£{displayTotal.toFixed(2)}</p>
                              {adjustmentType === 'add' && adjustmentTotal > 0 && (
                                <p className="text-xs text-primary">
                                  + £{adjustmentTotal.toFixed(2)} fee
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Invoice Total</p>
                        {(() => {
                          const invoiceTotal = reconcileInvoice.total_amount || 0;
                          const adjustmentTotal = (adjustmentAmount || 0) + (adjustmentVat || 0);
                          // Rebate: ADD to invoice (supplier gave discount, invoice is lower)
                          const displayTotal = adjustmentType === 'reduce' ? invoiceTotal + adjustmentTotal : invoiceTotal;
                          return (
                            <>
                              <p className="text-xl font-bold font-mono">£{displayTotal.toFixed(2)}</p>
                              {adjustmentType === 'reduce' && adjustmentTotal > 0 && (
                                <p className="text-xs text-green-500">
                                  + £{adjustmentTotal.toFixed(2)} rebate
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Variance</p>
                        {(() => {
                          const notesTotal = availableDeliveryNotes
                            .filter(n => selectedDeliveryNoteIds.includes(n.id))
                            .reduce((sum, n) => sum + (n.total_amount || 0), 0);
                          const adjustmentTotal = (adjustmentAmount || 0) + (adjustmentVat || 0);
                          // Fee: adds to delivery notes; Rebate: adds to invoice
                          const adjustedNotesTotal = adjustmentType === 'add' ? notesTotal + adjustmentTotal : notesTotal;
                          const adjustedInvoiceTotal = adjustmentType === 'reduce' ? (reconcileInvoice.total_amount || 0) + adjustmentTotal : (reconcileInvoice.total_amount || 0);
                          const variance = adjustedInvoiceTotal - adjustedNotesTotal;
                          return (
                            <p className={`text-xl font-bold font-mono ${Math.abs(variance) > 0.01 ? 'text-amber-500' : 'text-green-500'}`}>
                              £{variance.toFixed(2)}
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Variance Notes */}
              {selectedDeliveryNoteIds.length > 0 && (
                <div className="space-y-2">
                  <Label>Variance Notes (if any)</Label>
                  <Input
                    value={varianceNotes}
                    onChange={(e) => setVarianceNotes(e.target.value)}
                    placeholder="Explain any variance between delivery notes and invoice"
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReconcile} disabled={saving || selectedDeliveryNoteIds.length === 0}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              Reconcile ({selectedDeliveryNoteIds.length} notes)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasesPage;
